/*
 * cleaned up version of createAction from redux-toolkit without bloat and only with typed content.
 * No default case, no matcher
 */

import {Action} from 'redux'

export type PayloadAction<P = any, T extends string = string> = {
	payload: P
	type: T
}

export type ActionMatcher<P, T extends string> = ((action: PayloadAction<unknown>) => action is PayloadAction<P, T>)

export interface BaseActionCreator<P, T extends string> {
	type: T
	match: ActionMatcher<P, T>
}

export interface ActionCreatorWithPreparedPayload<P, T extends string>
	extends BaseActionCreator<P, T> {
	(...args: any[]): PayloadAction<P, T>
}

export interface ActionCreatorWithOptionalPayload<P, T extends string = string>
	extends BaseActionCreator<P, T> {
	(payload?: P): PayloadAction<P, T>
}

export interface ActionCreatorWithoutPayload<T extends string = string>
	extends BaseActionCreator<undefined, T> {
	(noArgument: void): PayloadAction<undefined, T>
}

export interface ActionCreatorWithPayload<P, T extends string = string>
	extends BaseActionCreator<P, T> {
	(payload: P): PayloadAction<P, T>
}

export interface ActionCreatorWithNonInferrablePayload<T extends string = string>
	extends BaseActionCreator<unknown, T> {
	<PT extends unknown>(payload: PT): PayloadAction<PT, T>
}

export type PrepareAction<P> = ((...args: any[]) => { payload: P })
//////// BEGIN type helpers

type IsAny<T, True, False = never> =
		true | false extends (T extends never ? true : false) ? True : False

type IsUnknown<T, True, False = never> = unknown extends T
	? IsAny<T, False, True>
	: False

type FallbackIfUnknown<T, Fallback> = IsUnknown<T, Fallback, T>

type IfMaybeUndefined<P, True, False> = [undefined] extends [P]
	? True
	: False

type IfVoid<P, True, False> = [void] extends [P] ? True : False

type IsEmptyObj<T, True, False = never> = T extends any
	? keyof T extends never
		? IsUnknown<T, False, IfMaybeUndefined<T, False, IfVoid<T, False, True>>>
		: False
	: never

type AtLeastTS35<True, False> = [True, False][IsUnknown<
	ReturnType<<T>() => T>,
	0,
	1
>]

type IsUnknownOrNonInferrable<T, True, False> = AtLeastTS35<
	IsUnknown<T, True, False>,
	IsEmptyObj<T, True, IsUnknown<T, True, False>>
>
type IfPrepareActionMethodProvided<
	PA extends PrepareAction<any> | void,
	True,
	False
> = PA extends (...args: any[]) => any ? True : False

//////// END type helpers

export type PayloadActionCreator<
	P = void,
	T extends string = string,
	PA extends PrepareAction<P> | void = void
> = IfPrepareActionMethodProvided<
	PA,
	ActionCreatorWithPreparedPayload<P, T>,
	// else
	IsAny<
		P,
		ActionCreatorWithPayload<any, T>,
		IsUnknownOrNonInferrable<
			P,
			ActionCreatorWithNonInferrablePayload<T>,
			// else
			IfVoid<
				P,
				ActionCreatorWithoutPayload<T>,
				// else
				IfMaybeUndefined<
					P,
					ActionCreatorWithOptionalPayload<P, T>,
					// else
					ActionCreatorWithPayload<P, T>
				>
			>
		>
	>
>

export function createAction<P = void, T extends string = string>(type: T): PayloadActionCreator<P, T>;
export function createAction<P = void, T extends string = string>(type: T): PayloadActionCreator<P, T>;
export function createAction<PA extends PrepareAction<any>, T extends string = string>(type: T, prepareAction: PA): PayloadActionCreator<ReturnType<PA>['payload'], T, PA>

export function createAction(type: string, payloadCreator?: Function): any {
	function actionCreator(...args: any[]) {
		if (payloadCreator) return {type, payload: payloadCreator(...args).payload}
		return {type, payload: args[0]}
	}

	actionCreator.toString = () => `${type}`
	actionCreator.type = type
	actionCreator.match = (action: Action<unknown>): action is PayloadAction => action.type === type
	return actionCreator
}